import { createElement, Fragment, type CSSProperties, type ReactNode } from "react";
import { BRANDBOOK } from "@/lib/brand/brandbook";

const ALLOWED_TAGS = new Set([
  "span",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "strike",
]);

export const LANDING_FONT_FAMILIES: { label: string; value: string }[] = [
  { label: "Брендбук (Arial)", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Системный", value: "system-ui, sans-serif" },
];

export const LANDING_COLOR_LIGHT_VAR = "--landing-color-light";
export const LANDING_COLOR_DARK_VAR = "--landing-color-dark";
export const LANDING_SIZE_LIGHT_VAR = "--landing-size-light";
export const LANDING_SIZE_DARK_VAR = "--landing-size-dark";
export const LANDING_WEIGHT_LIGHT_VAR = "--landing-weight-light";
export const LANDING_WEIGHT_DARK_VAR = "--landing-weight-dark";
export const LANDING_STYLE_LIGHT_VAR = "--landing-style-light";
export const LANDING_STYLE_DARK_VAR = "--landing-style-dark";
export const LANDING_DECO_LIGHT_VAR = "--landing-deco-light";
export const LANDING_DECO_DARK_VAR = "--landing-deco-dark";
export const LANDING_FONT_LIGHT_VAR = "--landing-font-light";
export const LANDING_FONT_DARK_VAR = "--landing-font-dark";
export const LANDING_RICH_COLOR_CLASS = "landing-rich-color";
export const LANDING_RICH_THEME_CLASS = "landing-rich-theme";

export const LANDING_THEME_STYLE_VARS = [
  LANDING_COLOR_LIGHT_VAR,
  LANDING_COLOR_DARK_VAR,
  LANDING_SIZE_LIGHT_VAR,
  LANDING_SIZE_DARK_VAR,
  LANDING_WEIGHT_LIGHT_VAR,
  LANDING_WEIGHT_DARK_VAR,
  LANDING_STYLE_LIGHT_VAR,
  LANDING_STYLE_DARK_VAR,
  LANDING_DECO_LIGHT_VAR,
  LANDING_DECO_DARK_VAR,
  LANDING_FONT_LIGHT_VAR,
  LANDING_FONT_DARK_VAR,
] as const;

export const LANDING_TEXT_COLOR_PRESETS: { label: string; value: string }[] = [
  { label: "Текст", value: BRANDBOOK.text },
  { label: "Вторичный", value: BRANDBOOK.textMuted },
  { label: "Акцент", value: BRANDBOOK.rose },
  { label: "Hover", value: BRANDBOOK.red },
  { label: "Белый", value: BRANDBOOK.white },
];

export const LANDING_TEXT_COLOR_PRESETS_DARK: { label: string; value: string }[] =
  [
    { label: "Белый", value: BRANDBOOK.white },
    { label: "Светлый", value: BRANDBOOK.mist },
    { label: "Вторичный", value: BRANDBOOK.textMuted },
    { label: "Акцент", value: BRANDBOOK.rose },
    { label: "Hover", value: BRANDBOOK.red },
  ];

const FONT_FAMILY_ALLOW = new Set(
  LANDING_FONT_FAMILIES.map((item) => item.value).filter(Boolean),
);

type RichAttrs = {
  href?: string;
  target?: string;
  rel?: string;
};

type RichNode =
  | { kind: "text"; value: string }
  | { kind: "br" }
  | {
      kind: "el";
      tag: string;
      style?: CSSProperties;
      attrs?: RichAttrs;
      children: RichNode[];
    };

function decodeEntities(raw: string): string {
  return raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, n: string) =>
      String.fromCodePoint(Number(n) & 0x10ffff),
    )
    .replace(/&#x([0-9a-f]+);/gi, (_, n: string) =>
      String.fromCodePoint(parseInt(n, 16) & 0x10ffff),
    );
}

function attrStyle(rawAttrs: string): string {
  const match = rawAttrs.match(/\bstyle\s*=\s*("([^"]*)"|'([^']*)')/i);
  return match?.[2] ?? match?.[3] ?? "";
}

function isAllowedThemeStyleValue(prop: string, value: string): boolean {
  if (prop === LANDING_COLOR_LIGHT_VAR || prop === LANDING_COLOR_DARK_VAR) {
    return normalizeBackgroundColorLike(value);
  }
  if (prop === LANDING_SIZE_LIGHT_VAR || prop === LANDING_SIZE_DARK_VAR) {
    return /^\d+(?:\.\d+)?px$/i.test(value);
  }
  if (prop === LANDING_WEIGHT_LIGHT_VAR || prop === LANDING_WEIGHT_DARK_VAR) {
    return /^(bold|normal|[1-9]00)$/i.test(value);
  }
  if (prop === LANDING_STYLE_LIGHT_VAR || prop === LANDING_STYLE_DARK_VAR) {
    return /^(italic|normal)$/i.test(value);
  }
  if (prop === LANDING_DECO_LIGHT_VAR || prop === LANDING_DECO_DARK_VAR) {
    const tokens = value.toLowerCase().split(/\s+/);
    return (
      tokens.length > 0 &&
      tokens.every((token) =>
        ["underline", "line-through", "none"].includes(token),
      )
    );
  }
  if (prop === LANDING_FONT_LIGHT_VAR || prop === LANDING_FONT_DARK_VAR) {
    return FONT_FAMILY_ALLOW.has(value);
  }
  return false;
}

function parseSafeStyle(raw: string): CSSProperties | undefined {
  const style: CSSProperties = {};
  for (const part of raw.split(";")) {
    const idx = part.indexOf(":");
    if (idx < 0) continue;
    const prop = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (!value || /expression|javascript|url\s*\(/i.test(value)) continue;
    if (prop === "color" && normalizeBackgroundColorLike(value)) {
      style.color = value;
    } else if (
      LANDING_THEME_STYLE_VARS.includes(
        prop as (typeof LANDING_THEME_STYLE_VARS)[number],
      ) &&
      isAllowedThemeStyleValue(prop, value)
    ) {
      (style as Record<string, string>)[prop] = value;
    } else if (prop === "font-size") {
      const px = value.match(/^(\d+(?:\.\d+)?)px$/i);
      if (px) {
        const n = Math.round(Number(px[1]));
        if (n >= 8 && n <= 96) style.fontSize = `${n}px`;
      }
    } else if (prop === "font-weight") {
      if (/^(bold|normal|[1-9]00)$/i.test(value)) style.fontWeight = value;
    } else if (prop === "font-style") {
      if (/^(italic|normal|oblique)$/i.test(value)) style.fontStyle = value;
    } else if (prop === "text-decoration") {
      const allowed = value
        .toLowerCase()
        .split(/\s+/)
        .filter((token) =>
          ["underline", "line-through", "none"].includes(token),
        );
      if (allowed.length) style.textDecoration = allowed.join(" ");
    } else if (prop === "font-family" && FONT_FAMILY_ALLOW.has(value)) {
      style.fontFamily = value;
    }
  }
  return Object.keys(style).length ? style : undefined;
}

function normalizeBackgroundColorLike(value: string): boolean {
  return (
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value) ||
    /^rgba?\(/i.test(value) ||
    /^hsla?\(/i.test(value) ||
    /^oklch\(/i.test(value) ||
    /^lab\(/i.test(value) ||
    /^color\(/i.test(value)
  );
}

function attrQuoted(rawAttrs: string, name: string): string | undefined {
  const match = rawAttrs.match(
    new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i"),
  );
  return match?.[2] ?? match?.[3];
}

function sanitizeLandingHref(href: string): string | undefined {
  const v = href.trim();
  if (!v || /^\s*javascript:/i.test(v)) return undefined;
  if (
    v.startsWith("/") ||
    v.startsWith("#") ||
    v.startsWith("mailto:") ||
    v.startsWith("tel:")
  ) {
    return v;
  }
  if (/^https:\/\//i.test(v)) return v;
  return undefined;
}

function parseElementAttrs(tag: string, rawAttrs: string): RichAttrs | undefined {
  if (tag !== "a") return undefined;
  const href = attrQuoted(rawAttrs, "href");
  const safeHref = href ? sanitizeLandingHref(href) : undefined;
  if (!safeHref) return undefined;
  const target = attrQuoted(rawAttrs, "target");
  const rel = attrQuoted(rawAttrs, "rel");
  return {
    href: safeHref,
    ...(target === "_blank"
      ? { target: "_blank", rel: rel ?? "noreferrer" }
      : rel
        ? { rel }
        : {}),
  };
}

function semanticStyle(tag: string): CSSProperties | undefined {
  if (tag === "b" || tag === "strong") return { fontWeight: 700 };
  if (tag === "i" || tag === "em") return { fontStyle: "italic" };
  if (tag === "u") return { textDecoration: "underline" };
  if (tag === "s" || tag === "strike") return { textDecoration: "line-through" };
  return undefined;
}

function parseRichHtml(input: string): RichNode[] {
  const src = String(input ?? "").replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
  const root: RichNode[] = [];
  const stack: {
    tag: string;
    style?: CSSProperties;
    attrs?: RichAttrs;
    children: RichNode[];
  }[] = [];
  const current = () =>
    stack.length ? stack[stack.length - 1].children : root;

  const re = /<(\/)?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)\/?>|([^<]+)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(src))) {
    if (match[4]) {
      current().push({ kind: "text", value: decodeEntities(match[4]) });
      continue;
    }
    const closing = Boolean(match[1]);
    const tag = match[2].toLowerCase();
    const rawAttrs = match[3] ?? "";
    if (!ALLOWED_TAGS.has(tag)) continue;
    if (tag === "br") {
      if (!closing) current().push({ kind: "br" });
      continue;
    }
    if (closing) {
      const idx = stack.findLastIndex((item) => item.tag === tag);
      if (idx < 0) continue;
      while (stack.length - 1 > idx) {
        const inner = stack.pop()!;
        current().push({
          kind: "el",
          tag: inner.tag,
          style: inner.style,
          attrs: inner.attrs,
          children: inner.children,
        });
      }
      const el = stack.pop()!;
      current().push({
        kind: "el",
        tag: el.tag,
        style: el.style,
        attrs: el.attrs,
        children: el.children,
      });
      continue;
    }
    const style =
      tag === "span" ? parseSafeStyle(attrStyle(rawAttrs)) : semanticStyle(tag);
    const attrs = parseElementAttrs(tag, rawAttrs);
    stack.push({ tag, style, attrs, children: [] });
  }
  while (stack.length) {
    const el = stack.pop()!;
    current().push({
      kind: "el",
      tag: el.tag,
      style: el.style,
      children: el.children,
    });
  }
  return root;
}

function nodesToPlain(nodes: RichNode[]): string {
  return nodes
    .map((node) => {
      if (node.kind === "text") return node.value;
      if (node.kind === "br") return "\n";
      return nodesToPlain(node.children);
    })
    .join("");
}

function spanHasThemeTypo(rec: Record<string, unknown>): boolean {
  return LANDING_THEME_STYLE_VARS.some(
    (key) =>
      key !== LANDING_COLOR_LIGHT_VAR &&
      key !== LANDING_COLOR_DARK_VAR &&
      typeof rec[key] === "string",
  );
}

function spanRenderProps(style?: CSSProperties): {
  style?: CSSProperties;
  className?: string;
} {
  if (!style) return {};
  const rec = { ...style } as Record<string, unknown>;
  const color = typeof rec.color === "string" ? rec.color : undefined;
  const light =
    typeof rec[LANDING_COLOR_LIGHT_VAR] === "string"
      ? (rec[LANDING_COLOR_LIGHT_VAR] as string)
      : color;
  const dark =
    typeof rec[LANDING_COLOR_DARK_VAR] === "string"
      ? (rec[LANDING_COLOR_DARK_VAR] as string)
      : undefined;
  const themeTypo = spanHasThemeTypo(rec);
  if (!light && !dark && !themeTypo) return { style };
  delete rec.color;
  if (light) rec[LANDING_COLOR_LIGHT_VAR] = light;
  if (dark) rec[LANDING_COLOR_DARK_VAR] = dark;
  const className = [
    light || dark ? LANDING_RICH_COLOR_CLASS : "",
    themeTypo ? LANDING_RICH_THEME_CLASS : "",
  ]
    .filter(Boolean)
    .join(" ");
  return {
    style: rec as CSSProperties,
    ...(className ? { className } : {}),
  };
}

function nodesToReact(nodes: RichNode[], keyPrefix = "r"): ReactNode {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.kind === "text") return node.value;
    if (node.kind === "br") return createElement("br", { key });
    const children = nodesToReact(node.children, key);
    if (node.tag === "span") {
      const props = spanRenderProps(node.style);
      return createElement(
        "span",
        { key, style: props.style, className: props.className },
        children,
      );
    }
    if (node.tag === "strong" || node.tag === "b") {
      return createElement("strong", { key, style: node.style }, children);
    }
    if (node.tag === "em" || node.tag === "i") {
      return createElement("em", { key, style: node.style }, children);
    }
    if (node.tag === "u") {
      return createElement("u", { key, style: node.style }, children);
    }
    if (node.tag === "a" && node.attrs?.href) {
      return createElement(
        "a",
        {
          key,
          href: node.attrs.href,
          ...(node.attrs.target
            ? { target: node.attrs.target, rel: node.attrs.rel }
            : {}),
          className: "text-primary underline-offset-2 hover:underline",
        },
        children,
      );
    }
    if (node.tag === "ul") {
      return createElement(
        "ul",
        { key, className: "my-2 list-disc space-y-2 ps-5" },
        children,
      );
    }
    if (node.tag === "ol") {
      return createElement(
        "ol",
        { key, className: "my-2 list-decimal space-y-2 ps-5" },
        children,
      );
    }
    if (node.tag === "li") {
      return createElement(
        "li",
        { key, className: "break-words [overflow-wrap:anywhere]" },
        children,
      );
    }
    if (node.tag === "p") {
      return createElement(
        "p",
        { key, className: "break-words [overflow-wrap:anywhere]" },
        children,
      );
    }
    return createElement("s", { key, style: node.style }, children);
  });
}

function hasMarkup(nodes: RichNode[]): boolean {
  return nodes.some((node) => {
    if (node.kind === "br") return true;
    if (node.kind === "el") return true;
    return false;
  });
}

/** Chrome/Firefox кладут Enter в div/p — сводим к <br>. */
export function normalizeEditorHtml(html: string): string {
  return html
    .replace(/&nbsp;/gi, " ")
    .replace(/<div><br\s*\/?><\/div>/gi, "<br/>")
    .replace(/<p><br\s*\/?><\/p>/gi, "<br/>")
    .replace(/<\/div>/gi, "")
    .replace(/<div>/gi, "<br/>")
    .replace(/<\/p>/gi, "")
    .replace(/<p>/gi, "<br/>")
    .replace(/^(<br\s*\/?>)+/i, "")
    .replace(/(<br\s*\/?>)+$/i, "");
}

export function landingHtmlToPlain(html: string): string {
  return nodesToPlain(parseRichHtml(html)).replace(/\n+$/g, "");
}

export function landingHtmlHasMarkup(html: string | undefined): boolean {
  if (!html) return false;
  return hasMarkup(parseRichHtml(html));
}

export function renderLandingHtml(html: string): ReactNode {
  const nodes = parseRichHtml(html);
  if (!nodes.length) return null;
  return createElement(Fragment, null, nodesToReact(nodes));
}

export function renderPlainWithBreaks(text: string): ReactNode {
  const lines = text.split("\n");
  if (lines.length <= 1) return text;
  return createElement(
    Fragment,
    null,
    lines.flatMap((line, index) =>
      index === 0
        ? [line]
        : [createElement("br", { key: `br-${index}` }), line],
    ),
  );
}

export function escapeTextToHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>");
}
